import { create } from 'zustand';
import { immer } from 'zustand/middleware/immer';
import { persist } from 'zustand/middleware';
import type { ToolDefinition, BodyPart } from './toolStore';
import { generateToolId, createPreBuiltBodyPartTools } from './toolStore';

// Re-export types for convenience when working with Puppet
export type { ToolDefinition, EasingFunction } from './toolStore';

// Body parts that have motors (excludes 'control')
export type MotorBodyPart =
  | 'head'
  | 'body'
  | 'leftArm'
  | 'rightArm'
  | 'leftLeg'
  | 'rightLeg';

// Axis of rotation for a motor
export type MotorAxis = 'x' | 'y' | 'z';

// Motor configuration for a single body part
export interface MotorConfig {
  id: string;
  bodyPart: MotorBodyPart;
  axis: MotorAxis;
  minAngle: number; // degrees
  maxAngle: number; // degrees
  /**
   * Bone offset in degrees - compensates for GLTF model rest pose
   *
   * GLTF models typically come in T-pose (arms horizontal at ~90°).
   * For string puppets, motor 0° should mean "relaxed/down" position.
   * The boneOffset converts motor degrees to bone rotation degrees.
   *
   * Formula: boneRotation = (motorDegrees * direction) + boneOffset
   *
   * Example for arms:
   * - GLTF T-pose has arms at 0° bone rotation (horizontal)
   * - String puppet motor 0° = arms down (needs -90° bone rotation)
   * - So boneOffset = -90 for arms
   *
   * @default 0
   */
  boneOffset?: number;
  /**
   * Direction multiplier for rotation (1 or -1)
   *
   * Controls whether increasing motor degrees rotates the bone
   * in the positive or negative direction on the axis.
   *
   * @default 1
   */
  direction?: 1 | -1;
  /**
   * Static offset for X axis in degrees (applied regardless of controlled axis)
   * Used to correct rest pose on non-controlled axes
   * @default 0
   */
  xOffset?: number;
  /**
   * Static offset for Y axis in degrees (applied regardless of controlled axis)
   * Used to correct rest pose on non-controlled axes
   * @default 0
   */
  yOffset?: number;
  /**
   * Static offset for Z axis in degrees (applied regardless of controlled axis)
   * Used to correct rest pose on non-controlled axes
   * @default 0
   */
  zOffset?: number;
}

// Position state for all motors (rotation angles in degrees)
export interface MotorPositions {
  head: number;
  body: number;
  leftArm: number;
  rightArm: number;
  leftLeg: number;
  rightLeg: number;
}

// Per-tool position configuration
export interface ToolPositionConfig {
  docking: number;
  starting: number;
}

/**
 * Bone mapping configuration for a puppet
 * Maps body parts to bone names in the 3D model skeleton
 */
export interface BoneMapping {
  head: string;
  body: string;
  leftArm: string;
  rightArm: string;
  leftLeg: string;
  rightLeg: string;
}

/**
 * Pending position action type
 * Used to signal the 3D model to apply positions
 */
export type PendingPositionAction = 'reset' | 'dock' | null;

// Puppet configuration (legacy - for motor/hardware config)
export interface PuppetConfig {
  id: string;
  name: string;
  modelPath: string;
  motors: MotorConfig[];
  dockingPosition: MotorPositions;
  startingPosition: MotorPositions;
  /** Per-tool position configuration (docking and starting positions) */
  toolPositions: Record<string, ToolPositionConfig>;
  /** Movement speed in degrees per second (default: 90) */
  movementSpeed: number;
  /** Bone mapping for 3D model skeleton */
  boneMapping: BoneMapping;
}

/**
 * Puppet interface for the puppet-owned tool architecture
 *
 * A puppet owns a set of animation tools and represents a character
 * that can be animated in the timeline.
 */
export interface Puppet {
  /** Auto-generated unique identifier */
  id: string;
  /** Human-readable name for the puppet */
  name: string;
  /** Path to the 3D model file */
  modelPath: string;
  /** Optional description text */
  description?: string;
  /** Optional reference URL */
  url?: string;
  /** Array of tools owned by this puppet */
  tools: ToolDefinition[];
}

/**
 * Generate a unique puppet ID
 * Pattern: puppet-{timestamp}-{random}
 */
export const generatePuppetId = (): string => {
  return `puppet-${Date.now()}-${Math.random().toString(36).substring(2, 9)}`;
};

// Re-export generateToolId for convenience
export { generateToolId } from './toolStore';

/** Starter Puppet ID - fixed ID for backward compatibility */
const STARTER_PUPPET_ID = 'starter-puppet';

/**
 * Create the Starter Puppet with body part tools only
 *
 * This puppet is created on first app load when no puppets exist.
 * It contains 6 body part tools (not control tools - those are global).
 * Tool IDs are preserved for backward compatibility with saved scenes.
 */
const createStarterPuppet = (): Puppet => {
  const prebuiltTools = createPreBuiltBodyPartTools();

  return {
    id: STARTER_PUPPET_ID,
    name: 'Starter Puppet',
    modelPath: '',
    description: 'Default puppet with standard animation tools',
    tools: prebuiltTools,
  };
};

/**
 * Migrate existing puppets to remove control tools
 *
 * Control tools (Wait, Stop) are now global and should not be stored
 * in puppet.tools. This migration removes any control tools that may
 * have been stored in older versions.
 */
const migrateRemoveControlTools = (): void => {
  const state = usePuppetStore.getState();
  let needsUpdate = false;

  const migratedPuppets = state.puppets.map((puppet) => {
    const filteredTools = puppet.tools.filter(
      (tool) => tool.bodyPart !== 'control'
    );
    if (filteredTools.length !== puppet.tools.length) {
      needsUpdate = true;
      return { ...puppet, tools: filteredTools };
    }
    return puppet;
  });

  if (needsUpdate) {
    usePuppetStore.setState({ puppets: migratedPuppets });
  }
};

/**
 * Initialize the Starter Puppet if no puppets exist
 *
 * This function is called on store creation and after rehydration
 * from localStorage to ensure there is always at least one puppet.
 * It is idempotent - calling it multiple times will not create duplicates.
 */
export const initializeStarterPuppet = (): void => {
  const state = usePuppetStore.getState();

  // Only create Starter Puppet if puppets array is empty
  if (state.puppets.length === 0) {
    const starterPuppet = createStarterPuppet();

    usePuppetStore.setState({
      puppets: [starterPuppet],
      activePuppetId: STARTER_PUPPET_ID,
      // Also initialize motor/position config
      activePuppet: createDefaultPuppet(),
    });
  } else if (!state.activePuppet) {
    // Ensure activePuppet config exists for motor positions/speeds
    usePuppetStore.setState({
      activePuppet: createDefaultPuppet(),
    });
  }

  // Always run migration to remove control tools from existing puppets
  migrateRemoveControlTools();
};

interface PuppetState {
  // Legacy state for motor/hardware config
  activePuppet: PuppetConfig | null;

  // New state for puppet-owned tools architecture
  puppets: Puppet[];
  activePuppetId: string | null;

  /**
   * Pending position action for 3D model to process
   * Set by control panel buttons, consumed by animation hook
   */
  pendingPositionAction: PendingPositionAction;
}

interface PuppetActions {
  // Legacy actions for motor/hardware config
  setActivePuppet: (puppet: PuppetConfig | null) => void;
  getMotorConfig: (bodyPart: string) => MotorConfig | undefined;
  /**
   * Update motor config for a body part (offset, direction, axis, xOffset, zOffset, etc.)
   * Used for calibrating GLTF model rest pose in Position Control Panel
   */
  updateMotorConfig: (
    bodyPart: MotorBodyPart,
    updates: Partial<
      Pick<
        MotorConfig,
        | 'boneOffset'
        | 'direction'
        | 'axis'
        | 'xOffset'
        | 'yOffset'
        | 'zOffset'
        | 'minAngle'
        | 'maxAngle'
      >
    >
  ) => void;
  clampRotation: (bodyPart: string, angle: number) => number;
  getDefaultPuppet: () => PuppetConfig;
  /** Get bone name for a body part from the active puppet's bone mapping */
  getBoneName: (bodyPart: MotorBodyPart) => string | undefined;

  // Position configuration actions
  getToolPosition: (toolId: string) => ToolPositionConfig;
  setToolPosition: (toolId: string, docking: number, starting: number) => void;
  setMovementSpeed: (speed: number) => void;

  // Body part position update actions (Task Group 5.6)
  /**
   * Update a single body part's docking position
   * @param bodyPart - The body part to update
   * @param angle - The angle in degrees
   */
  updateDockingPosition: (bodyPart: MotorBodyPart, angle: number) => void;

  /**
   * Update a single body part's starting position
   * @param bodyPart - The body part to update
   * @param angle - The angle in degrees
   */
  updateStartingPosition: (bodyPart: MotorBodyPart, angle: number) => void;

  // Position control actions (Task Group 4.3)
  /**
   * Set all motors to their starting positions
   * Triggers immediate position update in 3D model
   */
  resetToStartPosition: () => void;

  /**
   * Set all motors to their docking positions
   * Triggers immediate position update in 3D model
   */
  dockPuppet: () => void;

  /**
   * Clear the pending position action after it has been processed
   */
  clearPendingPositionAction: () => void;

  // Puppet management actions
  createPuppet: (data: Partial<Puppet>) => string;
  updatePuppet: (id: string, updates: Partial<Puppet>) => void;
  deletePuppet: (id: string) => boolean;
  setActivePuppetById: (id: string) => void;
  getActivePuppetData: () => Puppet | null;

  // Tool management actions within puppets
  addTool: (puppetId: string, tool: Partial<ToolDefinition>) => string;
  updateTool: (
    puppetId: string,
    toolId: string,
    updates: Partial<ToolDefinition>
  ) => void;
  deleteTool: (puppetId: string, toolId: string) => boolean;
  duplicateTool: (puppetId: string, toolId: string) => string;

  // Helper selectors
  getActivePuppetTools: () => ToolDefinition[];
  getToolById: (toolId: string) => ToolDefinition | undefined;
  getPuppetById: (id: string) => Puppet | undefined;
  /**
   * Get the display number for a puppet (1-indexed position in array)
   * Returns undefined if puppet not found
   */
  getPuppetNumber: (puppetId: string) => number | undefined;
}

type PuppetStore = PuppetState & PuppetActions;

/**
 * Default docking positions (safe transport pose)
 *
 * String puppet docking positions - all at 0:
 * - Head: 0 (nodded down)
 * - Body: 0 (turned left)
 * - Arms: 0 (down)
 * - Legs: 0 (down)
 */
const createDefaultPositions = (): MotorPositions => ({
  head: 0,
  body: 0,
  leftArm: 0,
  rightArm: 0,
  leftLeg: 0,
  rightLeg: 0,
});

/**
 * Default starting positions (performance-ready pose)
 *
 * String puppet starting positions:
 * - Head: 0 (nodded down)
 * - Body: 0 (turned left)
 * - Arms: 180 (raised to middle position)
 * - Legs: 0 (down)
 */
const createDefaultStartingPositions = (): MotorPositions => ({
  head: 0,
  body: 0,
  leftArm: 180,
  rightArm: 180,
  leftLeg: 0,
  rightLeg: 0,
});

/** Default movement speed in degrees per second */
const DEFAULT_MOVEMENT_SPEED = 90;

/**
 * Default bone mapping for the default puppet model
 *
 * Maps body parts to bone names in the skeleton.
 * These are the bone names from the default-puppet.glb model.
 */
const createDefaultBoneMapping = (): BoneMapping => ({
  head: 'Skeleton_neck_joint_1',
  body: 'Skeleton_torso_joint_1',
  leftArm: 'Skeleton_arm_joint_L__4_',
  rightArm: 'Skeleton_arm_joint_R',
  leftLeg: 'leg_joint_L_1',
  rightLeg: 'leg_joint_R_1',
});

/**
 * Create default puppet configuration with 6 body part motors
 *
 * Hardware-specific degree ranges (string puppet):
 * - Head: 0-90° (0 = nodded down, 90 = straight/up)
 * - Body: 0-180° (0 = turned left, 90 = center, 180 = turned right)
 * - Arms: 0-180° (0 = down, 90 = middle, 180 = up)
 * - Legs: 0-90° (0 = straight down, 90 = knee bent)
 *
 * The default puppet model (default-puppet.glb) is already in a resting pose,
 * so no boneOffset is needed. For models with T-pose rest position, use
 * boneOffset to compensate (e.g., boneOffset: -90 for arms).
 *
 * All docking and starting positions default to 0 (relaxed string puppet state).
 */
const createDefaultPuppet = (): PuppetConfig => ({
  id: 'default-puppet',
  name: 'Default Puppet',
  modelPath: '',
  motors: [
    // Coordinate system: Y = shoulder-to-shoulder, X = through chest, Z = spine
    // Head: pivots at neck around Y axis (0 = nodded forward, 90 = straight up)
    // boneOffset -205, direction -1 compensates for GLTF model rest pose
    {
      id: 'head',
      bodyPart: 'head',
      axis: 'y',
      minAngle: 0,
      maxAngle: 90,
      boneOffset: -205,
      direction: -1,
    },
    // Body: twists around spine (Z axis)
    { id: 'body', bodyPart: 'body', axis: 'z', minAngle: 0, maxAngle: 270 },
    // Arms: pivot at shoulder around Z axis
    {
      id: 'leftArm',
      bodyPart: 'leftArm',
      axis: 'z',
      minAngle: 0,
      maxAngle: 270,
    },
    {
      id: 'rightArm',
      bodyPart: 'rightArm',
      axis: 'z',
      minAngle: 0,
      maxAngle: 270,
    },
    // Legs: pivot at hip, knee string pulls forward/back around Y axis
    // Lower leg section is uncontrolled (hangs naturally)
    {
      id: 'leftLeg',
      bodyPart: 'leftLeg',
      axis: 'y',
      minAngle: 0,
      maxAngle: 90,
    },
    {
      id: 'rightLeg',
      bodyPart: 'rightLeg',
      axis: 'y',
      minAngle: 0,
      maxAngle: 90,
    },
  ],
  dockingPosition: createDefaultPositions(),
  startingPosition: createDefaultStartingPositions(),
  toolPositions: {},
  movementSpeed: DEFAULT_MOVEMENT_SPEED,
  boneMapping: createDefaultBoneMapping(),
});

// Valid motor body parts for validation
const MOTOR_BODY_PARTS: MotorBodyPart[] = [
  'head',
  'body',
  'leftArm',
  'rightArm',
  'leftLeg',
  'rightLeg',
];

const isMotorBodyPart = (value: string): value is MotorBodyPart => {
  return MOTOR_BODY_PARTS.includes(value as MotorBodyPart);
};

/**
 * Get default position config for a body part based on motor limits
 */
const getDefaultBodyPartPosition = (
  bodyPart: MotorBodyPart
): ToolPositionConfig => {
  const defaultPositions = createDefaultPositions();
  const defaultValue = defaultPositions[bodyPart];
  return {
    docking: defaultValue,
    starting: defaultValue,
  };
};

export const usePuppetStore = create<PuppetStore>()(
  persist(
    immer((set, get) => ({
      // Initial state
      activePuppet: null,
      puppets: [],
      activePuppetId: null,
      pendingPositionAction: null,

      // Legacy actions
      setActivePuppet: (puppet) =>
        set((state) => {
          state.activePuppet = puppet;
        }),

      getMotorConfig: (bodyPart) => {
        const state = get();
        if (!state.activePuppet) {
          return undefined;
        }
        return state.activePuppet.motors.find((m) => m.bodyPart === bodyPart);
      },

      updateMotorConfig: (bodyPart, updates) =>
        set((state) => {
          if (!state.activePuppet) return;
          const motorIndex = state.activePuppet.motors.findIndex(
            (m) => m.bodyPart === bodyPart
          );
          if (motorIndex === -1) return;

          // Apply updates to the motor config
          const motor = state.activePuppet.motors[motorIndex];
          if (updates.boneOffset !== undefined) {
            motor.boneOffset = updates.boneOffset;
          }
          if (updates.direction !== undefined) {
            motor.direction = updates.direction;
          }
          if (updates.axis !== undefined) {
            motor.axis = updates.axis;
          }
          if (updates.xOffset !== undefined) {
            motor.xOffset = updates.xOffset;
          }
          if (updates.yOffset !== undefined) {
            motor.yOffset = updates.yOffset;
          }
          if (updates.zOffset !== undefined) {
            motor.zOffset = updates.zOffset;
          }
          if (updates.minAngle !== undefined) {
            motor.minAngle = updates.minAngle;
          }
          if (updates.maxAngle !== undefined) {
            motor.maxAngle = updates.maxAngle;
          }
        }),

      clampRotation: (bodyPart, angle) => {
        const state = get();
        if (!state.activePuppet || !isMotorBodyPart(bodyPart)) {
          return angle;
        }

        const motor = state.activePuppet.motors.find(
          (m) => m.bodyPart === bodyPart
        );
        if (!motor) {
          return angle;
        }

        return Math.max(motor.minAngle, Math.min(motor.maxAngle, angle));
      },

      getDefaultPuppet: () => createDefaultPuppet(),

      getBoneName: (bodyPart) => {
        const state = get();
        if (!state.activePuppet?.boneMapping) {
          // Fallback to default bone mapping
          const defaultMapping = createDefaultBoneMapping();
          return defaultMapping[bodyPart];
        }
        return state.activePuppet.boneMapping[bodyPart];
      },

      // Position configuration actions
      getToolPosition: (toolId) => {
        const state = get();
        if (!state.activePuppet) {
          // Return default position if no active puppet (all 0 for string puppet)
          return { docking: 0, starting: 0 };
        }

        // Check if tool has specific position config
        const toolPosition = state.activePuppet.toolPositions[toolId];
        if (toolPosition) {
          return toolPosition;
        }

        // Find tool to get its body part for defaults
        const tool = state.getToolById(toolId);
        if (
          tool &&
          tool.bodyPart !== 'control' &&
          isMotorBodyPart(tool.bodyPart)
        ) {
          return getDefaultBodyPartPosition(tool.bodyPart);
        }

        // Fallback to generic default (all 0 for string puppet)
        return { docking: 0, starting: 0 };
      },

      setToolPosition: (toolId, docking, starting) =>
        set((state) => {
          if (state.activePuppet) {
            state.activePuppet.toolPositions[toolId] = { docking, starting };
          }
        }),

      setMovementSpeed: (speed) =>
        set((state) => {
          if (state.activePuppet) {
            state.activePuppet.movementSpeed = speed;
          }
        }),

      // Body part position update actions (Task Group 5.6)
      updateDockingPosition: (bodyPart, angle) =>
        set((state) => {
          if (state.activePuppet) {
            state.activePuppet.dockingPosition[bodyPart] = angle;
          }
        }),

      updateStartingPosition: (bodyPart, angle) =>
        set((state) => {
          if (state.activePuppet) {
            state.activePuppet.startingPosition[bodyPart] = angle;
          }
        }),

      // Position control actions (Task Group 4.3)
      resetToStartPosition: () =>
        set((state) => {
          if (state.activePuppet) {
            state.pendingPositionAction = 'reset';
          }
        }),

      dockPuppet: () =>
        set((state) => {
          if (state.activePuppet) {
            state.pendingPositionAction = 'dock';
          }
        }),

      clearPendingPositionAction: () =>
        set((state) => {
          state.pendingPositionAction = null;
        }),

      // Puppet management actions
      createPuppet: (data) => {
        const id = data.id || generatePuppetId();
        const newPuppet: Puppet = {
          id,
          name: data.name || 'New Puppet',
          modelPath: data.modelPath || '',
          description: data.description,
          url: data.url,
          tools: data.tools || [],
        };

        set((state) => {
          state.puppets.push(newPuppet);
        });

        return id;
      },

      updatePuppet: (id, updates) => {
        set((state) => {
          const puppetIndex = state.puppets.findIndex((p) => p.id === id);
          if (puppetIndex !== -1) {
            // Update puppet properties but don't replace the entire object
            const puppet = state.puppets[puppetIndex];
            if (updates.name !== undefined) puppet.name = updates.name;
            if (updates.modelPath !== undefined)
              puppet.modelPath = updates.modelPath;
            if (updates.description !== undefined)
              puppet.description = updates.description;
            if (updates.url !== undefined) puppet.url = updates.url;
            if (updates.tools !== undefined) puppet.tools = updates.tools;
          }
        });
      },

      deletePuppet: (id) => {
        const state = get();
        // Cannot delete the last puppet
        if (state.puppets.length <= 1) {
          return false;
        }

        set((state) => {
          const puppetIndex = state.puppets.findIndex((p) => p.id === id);
          if (puppetIndex !== -1) {
            state.puppets.splice(puppetIndex, 1);

            // If the deleted puppet was active, switch to another puppet
            if (state.activePuppetId === id && state.puppets.length > 0) {
              state.activePuppetId = state.puppets[0].id;
            }
          }
        });

        return true;
      },

      setActivePuppetById: (id) => {
        set((state) => {
          const puppet = state.puppets.find((p) => p.id === id);
          if (puppet) {
            state.activePuppetId = id;
            // Initialize activePuppet config if not already set
            // This enables motor position/speed configuration
            if (!state.activePuppet) {
              state.activePuppet = createDefaultPuppet();
            }
          }
        });
      },

      getActivePuppetData: () => {
        const state = get();
        if (!state.activePuppetId) {
          return null;
        }
        return state.puppets.find((p) => p.id === state.activePuppetId) || null;
      },

      // Tool management actions within puppets
      addTool: (puppetId, tool) => {
        // Use provided ID if it's a prebuilt tool, otherwise generate new ID
        const toolId = tool.id || generateToolId();
        const newTool: ToolDefinition = {
          id: toolId,
          name: tool.name || 'New Tool',
          bodyPart: (tool.bodyPart as BodyPart) || 'control',
          type: 'prebuilt',
          defaultDuration: tool.defaultDuration ?? 1.0,
          defaultEasing: tool.defaultEasing || 'ease-in-out',
          minDegrees: tool.minDegrees ?? 0,
          maxDegrees: tool.maxDegrees ?? 90,
          dockingPosition: tool.dockingPosition ?? 0,
          startingPosition: tool.startingPosition ?? 0,
          color: tool.color || '#808080',
          isPrebuilt: tool.isPrebuilt ?? false,
          icon: tool.icon,
          httpEndpoints: tool.httpEndpoints,
          axis: tool.axis || 'x',
        };

        set((state) => {
          const puppet = state.puppets.find((p) => p.id === puppetId);
          if (puppet) {
            puppet.tools.push(newTool);
          }
        });

        return toolId;
      },

      updateTool: (puppetId, toolId, updates) => {
        set((state) => {
          const puppet = state.puppets.find((p) => p.id === puppetId);
          if (puppet) {
            const toolIndex = puppet.tools.findIndex((t) => t.id === toolId);
            if (toolIndex !== -1) {
              const tool = puppet.tools[toolIndex];
              if (updates.name !== undefined) tool.name = updates.name;
              if (updates.bodyPart !== undefined)
                tool.bodyPart = updates.bodyPart;
              if (updates.defaultDuration !== undefined)
                tool.defaultDuration = updates.defaultDuration;
              if (updates.defaultEasing !== undefined)
                tool.defaultEasing = updates.defaultEasing;
              if (updates.minDegrees !== undefined)
                tool.minDegrees = updates.minDegrees;
              if (updates.maxDegrees !== undefined)
                tool.maxDegrees = updates.maxDegrees;
              if (updates.dockingPosition !== undefined)
                tool.dockingPosition = updates.dockingPosition;
              if (updates.startingPosition !== undefined)
                tool.startingPosition = updates.startingPosition;
              if (updates.color !== undefined) tool.color = updates.color;
              if (updates.icon !== undefined) tool.icon = updates.icon;
              if (updates.httpEndpoints !== undefined)
                tool.httpEndpoints = updates.httpEndpoints;
              if (updates.axis !== undefined) tool.axis = updates.axis;
            }
          }
        });
      },

      deleteTool: (puppetId, toolId) => {
        const state = get();
        const puppet = state.puppets.find((p) => p.id === puppetId);
        if (!puppet) {
          return false;
        }

        const tool = puppet.tools.find((t) => t.id === toolId);
        if (!tool) {
          return false;
        }

        // Cannot delete prebuilt tools
        if (tool.isPrebuilt) {
          return false;
        }

        set((state) => {
          const puppet = state.puppets.find((p) => p.id === puppetId);
          if (puppet) {
            const toolIndex = puppet.tools.findIndex((t) => t.id === toolId);
            if (toolIndex !== -1) {
              puppet.tools.splice(toolIndex, 1);
            }
          }
        });

        return true;
      },

      duplicateTool: (puppetId, toolId) => {
        const state = get();
        const puppet = state.puppets.find((p) => p.id === puppetId);
        if (!puppet) {
          return '';
        }

        const originalTool = puppet.tools.find((t) => t.id === toolId);
        if (!originalTool) {
          return '';
        }

        const newToolId = generateToolId();
        const duplicatedTool: ToolDefinition = {
          ...originalTool,
          id: newToolId,
          name: `${originalTool.name} (copy)`,
          isPrebuilt: false, // Duplicated tools are never prebuilt
        };

        set((state) => {
          const puppet = state.puppets.find((p) => p.id === puppetId);
          if (puppet) {
            puppet.tools.push(duplicatedTool);
          }
        });

        return newToolId;
      },

      // Helper selectors
      getActivePuppetTools: () => {
        const state = get();
        if (!state.activePuppetId) {
          return [];
        }
        const puppet = state.puppets.find((p) => p.id === state.activePuppetId);
        return puppet?.tools || [];
      },

      getToolById: (toolId) => {
        const state = get();
        for (const puppet of state.puppets) {
          const tool = puppet.tools.find((t) => t.id === toolId);
          if (tool) {
            return tool;
          }
        }
        return undefined;
      },

      getPuppetById: (id) => {
        const state = get();
        return state.puppets.find((p) => p.id === id);
      },

      getPuppetNumber: (puppetId) => {
        const state = get();
        const index = state.puppets.findIndex((p) => p.id === puppetId);
        return index !== -1 ? index + 1 : undefined;
      },
    })),
    {
      name: 'puppet-studio-puppets',
      version: 2,
      partialize: (state) => ({
        activePuppet: state.activePuppet,
        puppets: state.puppets,
        activePuppetId: state.activePuppetId,
        // Note: pendingPositionAction is NOT persisted - it's transient state
      }),
      migrate: (persistedState, version) => {
        const state = persistedState as PuppetState;

        // Migration from version 0 (no version) to version 1
        // Add toolPositions and movementSpeed with defaults
        if (version === 0 || version === 1) {
          // Reset activePuppet to get new motor axis configurations
          // Version 2 changed body axis from x to y, so we need fresh config
          state.activePuppet = createDefaultPuppet();
        }

        // Ensure activePuppet has all required fields
        if (state.activePuppet) {
          const puppet = state.activePuppet as PuppetConfig;
          if (!puppet.toolPositions) {
            puppet.toolPositions = {};
          }
          if (puppet.movementSpeed === undefined) {
            puppet.movementSpeed = DEFAULT_MOVEMENT_SPEED;
          }
        }

        return state;
      },
      onRehydrateStorage: () => {
        return (state) => {
          // After rehydration, ensure activePuppet has new fields with defaults
          if (state?.activePuppet) {
            if (!state.activePuppet.toolPositions) {
              state.activePuppet.toolPositions = {};
            }
            if (state.activePuppet.movementSpeed === undefined) {
              state.activePuppet.movementSpeed = DEFAULT_MOVEMENT_SPEED;
            }
          }

          // Initialize Starter Puppet if needed
          // Use a small delay to ensure state is fully rehydrated
          setTimeout(() => {
            initializeStarterPuppet();
          }, 0);
        };
      },
    }
  )
);

// Initialize Starter Puppet on first store creation (non-persisted case)
// This runs synchronously when the module loads
setTimeout(() => {
  initializeStarterPuppet();
}, 0);
